import { useHandler } from "@redotech/react-util/hook";
import BracketEllipsesIcon from "@redotech/redo-icons/brackets-ellipses_filled.svg";
import FaceSmileIcon from "@redotech/redo-icons/face-smile.svg";
import {
  applyDynamicVariableSyntaxLiquid,
  DynamicVariable,
  SchemaType,
} from "@redotech/redo-model/advanced-flow/schemas/schemas";
import { SchemaField } from "@redotech/redo-model/advanced-flow/type-system/schema";
import { memo, RefObject, useRef, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { DynamicVariableSelectionModal } from "../../dynamic-variable-selection-modal";
import {
  BaseEmoji,
  CustomEmoji,
  EmojiSelectionModal,
} from "../../emoji-selection-modal";

export const EmailTextInputToolbar = memo(
  function EmailBuilderTextInputToolbar({
    text,
    setText,
    inputRef,
    schemaType,
    availableDynamicVariables,
  }: {
    text: string;
    setText: (value: string) => void;
    inputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
    schemaType: SchemaType | null;
    availableDynamicVariables: readonly SchemaField[];
  }) {
    const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
    const [variablePickerOpen, setVariablePickerOpen] = useState(false);
    const emojiButtonRef = useRef<HTMLButtonElement>(null);
    const variableButtonRef = useRef<HTMLButtonElement>(null);

    const handleEmojiPickerOpen = useHandler(() => {
      setEmojiPickerOpen(true);
    });

    const handleEmojiPicked = useHandler((emoji: BaseEmoji | CustomEmoji) => {
      if (!("native" in emoji)) {
        return; // Rule out CustomEmoji
      }

      const insertPosition = inputRef.current?.selectionStart ?? text.length;
      setText(
        text.slice(0, insertPosition) +
          emoji.native +
          text.slice(insertPosition),
      );
    });

    const handleVariablePicked = useHandler((variable: DynamicVariable) => {
      const variableText = applyDynamicVariableSyntaxLiquid({
        ...variable,
        isHtml: false,
        schemaType,
      });

      const insertPosition = inputRef.current?.selectionStart ?? text.length;
      setText(
        text.slice(0, insertPosition) +
          variableText +
          text.slice(insertPosition),
      );

      setVariablePickerOpen(false);
    });

    const handleVariablePickerClose = useHandler(() => {
      setVariablePickerOpen(false);
    });

    return (
      <>
        <RedoButton
          hierarchy="tertiary"
          IconLeading={FaceSmileIcon}
          onClick={handleEmojiPickerOpen}
          ref={emojiButtonRef}
          size="sm"
        />
        {schemaType && (
          <RedoButton
            hierarchy="tertiary"
            IconLeading={BracketEllipsesIcon}
            onClick={() => setVariablePickerOpen(!variablePickerOpen)}
            ref={variableButtonRef}
            size="sm"
          />
        )}
        <EmojiSelectionModal
          anchor={emojiButtonRef.current}
          handleEmojiSelected={handleEmojiPicked}
          onClose={() => setEmojiPickerOpen(false)}
          open={emojiPickerOpen}
          placement="left"
          theme="light"
        />
        {variablePickerOpen && (
          <DynamicVariableSelectionModal
            dynamicVariables={availableDynamicVariables}
            handleDynamicVariableSelected={handleVariablePicked}
            onClose={handleVariablePickerClose}
            open={variablePickerOpen}
          />
        )}
      </>
    );
  },
);
